import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { PieChart, Pie, ResponsiveContainer } from "recharts";
import {
  white,
  successGreen,
  incompleteRed,
  borderGrayLight,
  bgGrayLighter,
  darkDenium,
  valueBlack,
} from "../../constants/colors";
import { ChartLabelContainer } from "./DashboardStyles";

const MemberOverview = ({ data, loading }) => {
  const chartData = React.useMemo(() => {
    const pieData =
      loading || data.total === 0
        ? [{ name: "Loading", value: 1, color: bgGrayLighter }]
        : data.pieData;
    return pieData.map((d) => ({ ...d, fill: d.color ?? d.fill }));
  }, [loading, data.total, data.pieData]);

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        height: "100%",
        minHeight: "320px",
        bgcolor: white,
      }}
    >
      <Box
        sx={{
          height: "50px",
          px: "20px",
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid ${borderGrayLight}`,
        }}
      >
        <Typography
          sx={{ fontWeight: 500, fontSize: "16px", color: darkDenium }}
        >
          Opportunity Overview
        </Typography>
      </Box>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          minHeight: "260px",
          p: { xs: "20px 16px !important", sm: "0 24px !important" },
          flexGrow: 1,
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: { xs: 220, sm: 220 },
            height: { xs: 220, sm: 220 },
            flexShrink: 0,
            my: { xs: 0, sm: 2 },
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="90%"
                paddingAngle={loading ? 0 : 2.5}
                dataKey="value"
                stroke="none"
                startAngle={90}
                endAngle={-270}
              />
            </PieChart>
          </ResponsiveContainer>
          <ChartLabelContainer>
            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "13px" },
                color: valueBlack,
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              Total Opportunities
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "22px", sm: "24px" },
                color: darkDenium,
                fontWeight: 700,
              }}
            >
              {loading ? "-" : (data.total ?? "-")}
            </Typography>
          </ChartLabelContainer>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: { xs: "100%", sm: "auto" },
            minWidth: { sm: "170px" },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%", gap: 3 }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: successGreen,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontWeight: 500, fontSize: "16px", color: valueBlack }}
              >
                Complete
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: darkDenium,
                textAlign: "right",
              }}
            >
              {loading ? "-" : (data.complete ?? "-")}
            </Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%", gap: 3 }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: incompleteRed,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontWeight: 500, fontSize: "16px", color: valueBlack }}
              >
                Incomplete
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: darkDenium,
                textAlign: "right",
              }}
            >
              {loading ? "-" : (data.incomplete ?? "-")}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

MemberOverview.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    complete: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    incomplete: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pieData: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        color: PropTypes.string,
      }),
    ),
  }).isRequired,
  loading: PropTypes.bool,
};

export default MemberOverview;
